import React, { useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

const Stats = () => {
  const [counters, setCounters] = useState({
    schools: 0,
    students: 0,
    teachers: 0,
    satisfaction: 0
  });
  
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  const stats = [
    {
      icon: 'fas fa-school',
      number: 500,
      suffix: '+',
      label: 'Active Schools',
      key: 'schools'
    },
    {
      icon: 'fas fa-users',
      number: 50000,
      suffix: '+',
      label: 'Students Managed',
      key: 'students',
      format: (num) => `${Math.floor(num / 1000)}K+`
    },
    {
      icon: 'fas fa-chalkboard-teacher',
      number: 5000,
      suffix: '+',
      label: 'Teachers Connected',
      key: 'teachers',
      format: (num) => `${Math.floor(num / 1000)}K+`
    },
    {
      icon: 'fas fa-heart',
      number: 99,
      suffix: '%',
      label: 'Satisfaction Rate',
      key: 'satisfaction',
      highlight: true
    }
  ];

  useEffect(() => {
    if (isInView) {
      const animateCounters = () => {
        const duration = 2000;
        const steps = 60;
        const stepTime = duration / steps;

        let step = 0;
        const timer = setInterval(() => {
          step++;
          const progress = step / steps;
          
          setCounters({
            schools: Math.floor(500 * progress),
            students: Math.floor(50000 * progress),
            teachers: Math.floor(5000 * progress),
            satisfaction: Math.floor(99 * progress)
          });

          if (step >= steps) clearInterval(timer);
        }, stepTime);
      };

      animateCounters();
    }
  }, [isInView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="stats" ref={ref}>
      <div className="stats-container">
        <motion.div 
          className="stats-grid"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className={`stats-card ${stat.highlight ? 'highlight' : ''}`}
              variants={cardVariants}
              whileHover={{ 
                y: -5,
                transition: { duration: 0.3 }
              }}
            >
              <motion.div 
                className="stats-icon"
                whileHover={{ scale: 1.1 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <i className={stat.icon}></i>
              </motion.div>
              
              <div className="stats-content">
                <motion.div 
                  className="stats-number"
                  initial={{ scale: 0 }}
                  animate={isInView ? { scale: 1 } : { scale: 0 }}
                  transition={{ delay: 0.5 + index * 0.1, type: "spring" }}
                >
                  {stat.format ? stat.format(counters[stat.key]) : `${counters[stat.key]}${stat.suffix}`}
                </motion.div>
                <div className="stats-label">{stat.label}</div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Stats;